import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Form,
  Button,
  Space,
  Select,
  DatePicker,
  Input,
  InputNumber,
  Table,
  Row,
  Col,
  Typography,
  App,
  Spin,
  Alert,
  Checkbox,
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import api from '@/services/api.service';
import directPurchaseInvoiceService, {
  type CreateDirectPurchaseInvoicePayload,
  type DirectExpenseLineInput,
} from '@/services/directPurchaseInvoice.service';
import { useChartAccountsByType } from '@/hooks/useSalesInvoice';
import { useDepartmentsForSelection } from '@/hooks/useDepartments';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { LINE_SPECIFICATIONS_MAX_LENGTH } from '@/components/procurement/LineSpecifications';
import { useProjectsForSelection } from '@/hooks/useProject';
import recognitionService, { DAY_COUNT_METHODS, PARTIAL_MONTH_POLICIES, POSTING_MODES, RECOGNITION_METHODS } from '@/services/financeRecognition.service';
import { asObject } from '@/pages/finance/prepaid/prepaidDisplay';

const { Title, Text } = Typography;
const { TextArea } = Input;

type LineRow = DirectExpenseLineInput & { key: string };

const PREPAID = 'PREPAID_EXPENSE';
const LINE_METHODS = RECOGNITION_METHODS.filter((m) => m !== 'CUSTOM_SCHEDULE');

const METHOD_I18N: Record<string, string> = {
  DAILY_PRO_RATA: 'recognitionDailyProRata',
  STRAIGHT_LINE_MONTHLY: 'recognitionStraightLineMonthly',
  EQUAL_ACCOUNTING_PERIOD: 'recognitionEqualAccountingPeriod',
};

const DAY_COUNT_I18N: Record<string, string> = {
  ACTUAL_CALENDAR_DAYS: 'dayCountActualCalendarDays',
  ACTUAL_365: 'dayCountActual365',
  ACTUAL_366: 'dayCountActual366',
  THIRTY_DAY_MONTH: 'dayCountThirtyDayMonth',
  ACCOUNTING_PERIOD_DAYS: 'dayCountAccountingPeriodDays',
};

const PARTIAL_MONTH_I18N: Record<string, string> = {
  PRO_RATA: 'partialMonthProRata',
  FULL_MONTH: 'partialMonthFullMonth',
};

const POSTING_MODE_I18N: Record<string, string> = {
  MANUAL: 'postingModeManual',
  AUTO_CREATE_DRAFT: 'postingModeAutoCreateDraft',
  AUTO_POST: 'postingModeAutoPost',
};

function prepaidLineProblemKey(row: LineRow): string | null {
  if (row.expense_treatment !== PREPAID) return null;
  const cfg = row.prepaid_config ?? {};
  if (!row.prepaid_asset_account_id) return 'prepaidProblemSelectAsset';
  if (!cfg.service_start_date || !cfg.service_end_date) return 'prepaidProblemEnterDates';
  if (cfg.service_end_date < cfg.service_start_date) return 'prepaidProblemEndBeforeStart';
  return null;
}

function unwrap<T>(res: any): T {
  const data = res?.data ?? res;
  return (data?.data ?? data) as T;
}

const DirectPurchaseInvoiceFormPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id && id !== 'new';
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [lines, setLines] = useState<LineRow[]>([{ key: '1', expense_account_id: '', amount: 0 }]);
  const [dupState, setDupState] = useState<'block' | 'warn' | null>(null);

  const { data: existing, isLoading } = useQuery({
    queryKey: ['direct-purchase-invoice', id],
    queryFn: () => directPurchaseInvoiceService.getById(id!),
    enabled: isEdit,
  });

  const { data: suppliers = [] } = useQuery({
    queryKey: ['suppliers-list-dpi'],
    queryFn: async () => {
      const res = await api.get('/procurement/suppliers');
      return unwrap<any[]>(res) ?? [];
    },
  });

  const {
    data: vatConfigs = [],
    isError: vatConfigsError,
    error: vatConfigsLoadError,
  } = useQuery({
    queryKey: ['dpi-vat-configs'],
    queryFn: () => directPurchaseInvoiceService.getVatConfigurations(),
  });

  const { data: expenseAccounts = [] } = useChartAccountsByType('Expense', false);
  const { data: liabilityAccounts = [] } = useChartAccountsByType('Liability', false);
  const { data: assetAccounts = [] } = useChartAccountsByType('Asset', false);
  const { data: departments = [] } = useDepartmentsForSelection();
  const { data: projects = [] } = useProjectsForSelection();

  const apOptions = useMemo(
    () => liabilityAccounts.map((a) => ({ value: a.id, label: `${a.account_code} - ${a.account_name}` })),
    [liabilityAccounts]
  );
  const expenseOptions = useMemo(
    () => expenseAccounts.map((a) => ({ value: a.id, label: `${a.account_code} - ${a.account_name}` })),
    [expenseAccounts]
  );
  const prepaidAssetOptions = useMemo(
    () => assetAccounts.map((a) => ({ value: a.id, label: `${a.account_code} - ${a.account_name}` })),
    [assetAccounts]
  );
  const { data: prepaidCategories = [] } = useQuery({
    queryKey: ['recognition', 'categories'],
    queryFn: () => recognitionService.listCategories(),
    enabled: lines.some((l) => l.expense_treatment === PREPAID),
  });
  const vatOptions = useMemo(
    () =>
      (Array.isArray(vatConfigs) ? vatConfigs : []).map((v) => ({
        value: v.id,
        label: v.description
          ? `${v.description} (${Number(v.vat_rate)}%)`
          : `${v.vat_type} ${Number(v.vat_rate)}%`,
        rate: Number(v.vat_rate),
        suggestedGlAccountId: v.gl_account_id ?? v.gl_account?.id,
      })),
    [vatConfigs]
  );

  const inputVatGlOptions = useMemo(() => {
    const byId = new Map<string, { value: string; label: string }>();
    for (const a of [...assetAccounts, ...liabilityAccounts]) {
      byId.set(a.id, { value: a.id, label: `${a.account_code} - ${a.account_name}` });
    }
    return Array.from(byId.values());
  }, [assetAccounts, liabilityAccounts]);

  const projectOptions = useMemo(
    () =>
      (projects as any[]).map((p) => ({
        value: p.id,
        label: p.project_number ? `${p.project_number} - ${p.project_name}` : p.project_name ?? p.id,
      })),
    [projects]
  );

  const resolveDepartmentId = (departmentLabel?: string) => {
    if (!departmentLabel) return undefined;
    const d = departments.find(
      (x) =>
        departmentLabel === x.department_name ||
        departmentLabel === `${x.department_code} - ${x.department_name}`
    );
    return d?.id;
  };

  useEffect(() => {
    if (!existing || !isEdit) return;
    form.setFieldsValue({
      supplier_id: existing.supplier_id,
      supplier_invoice_number: existing.supplier_invoice_number,
      supplier_invoice_date: dayjs(existing.supplier_invoice_date),
      received_date: dayjs(existing.received_date),
      due_date: dayjs(existing.due_date),
      posting_date: existing.posting_date ? dayjs(existing.posting_date) : undefined,
      payment_terms: existing.payment_terms,
      debit_account_id: existing.debit_account_id,
      department_id: existing.department_id ?? resolveDepartmentId(existing.department),
      notes: existing.notes,
      discount_amount: existing.discount_amount,
      freight_charges: existing.freight_charges,
      other_charges: existing.other_charges,
      rounding_difference: existing.rounding_difference,
    });
    if (existing.lines?.length) {
      setLines(
        existing.lines.map((l, i) => ({
          key: l.id || String(i),
          expense_account_id: l.expense_account_id,
          item_description: l.item_description,
          specifications: l.specifications ?? null,
          quantity: l.quantity,
          unit_price: l.unit_price,
          amount: l.amount,
          vat_configuration_id: l.vat_configuration_id,
          input_vat_account_id: l.input_vat_account_id,
          vat_rate: l.vat_rate,
          tax_inclusive: l.tax_inclusive,
          tax_amount: l.tax_amount,
          project_id: l.project_id,
          expense_treatment: l.expense_treatment === PREPAID ? PREPAID : 'NORMAL_EXPENSE',
          prepaid_asset_account_id: l.prepaid_asset_account_id ?? null,
          prepaid_config: l.expense_treatment === PREPAID ? (asObject(l.prepaid_config) as LineRow['prepaid_config']) : null,
        }))
      );
    }
  }, [existing, isEdit, form, departments]);

  const lineTotals = useMemo(() => {
    let sub = 0;
    let tax = 0;
    let gross = 0;
    for (const l of lines) {
      const amt = Number(l.amount) || 0;
      const tAmt = Number(l.tax_amount) || 0;
      if (l.tax_inclusive) {
        sub += amt - tAmt;
        tax += tAmt;
        gross += amt;
      } else {
        sub += amt;
        tax += tAmt;
        gross += amt + tAmt;
      }
    }
    return { sub, tax, gross };
  }, [lines]);

  const saveMutation = useMutation({
    mutationFn: async (payload: CreateDirectPurchaseInvoicePayload) => {
      if (isEdit) return directPurchaseInvoiceService.update(id!, payload);
      return directPurchaseInvoiceService.create(payload);
    },
    onSuccess: (row) => {
      message.success(tc('successSaved'));
      queryClient.invalidateQueries({ queryKey: ['direct-purchase-invoices'] });
      navigate(`/fin/direct-purchase-invoices/${row.id}`);
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || t('saveFailed')),
  });

  const checkDuplicate = async (supplierId: string, num: string) => {
    if (!supplierId || !num) {
      setDupState(null);
      return;
    }
    try {
      const r = await directPurchaseInvoiceService.checkDuplicate(supplierId, num, isEdit ? id : undefined);
      if (r.duplicate) {
        setDupState(r.policy === 'block' ? 'block' : 'warn');
      } else setDupState(null);
    } catch {
      setDupState(null);
    }
  };

  const buildPayload = (values: any): CreateDirectPurchaseInvoicePayload => {
    const validLines = lines
      .filter((l) => l.expense_account_id && (Number(l.amount) > 0 || (l.quantity && l.unit_price)))
      .map(({ key: _k, ...rest }) =>
        rest.expense_treatment === PREPAID
          ? rest
          : { ...rest, expense_treatment: 'NORMAL_EXPENSE' as const, prepaid_asset_account_id: null, prepaid_config: null }
      );
    const dept = departments.find((d) => d.id === values.department_id);
    const departmentLabel = dept ? `${dept.department_code} - ${dept.department_name}` : undefined;
    return {
      supplier_id: values.supplier_id,
      supplier_invoice_number: values.supplier_invoice_number,
      supplier_invoice_date: dayjs(values.supplier_invoice_date).format('YYYY-MM-DD'),
      received_date: dayjs(values.received_date).format('YYYY-MM-DD'),
      due_date: dayjs(values.due_date).format('YYYY-MM-DD'),
      posting_date: values.posting_date ? dayjs(values.posting_date).format('YYYY-MM-DD') : undefined,
      payment_terms: values.payment_terms,
      debit_account_id: values.debit_account_id,
      department_id: values.department_id,
      department: departmentLabel,
      notes: values.notes,
      discount_amount: Number(values.discount_amount ?? 0),
      freight_charges: Number(values.freight_charges ?? 0),
      other_charges: Number(values.other_charges ?? 0),
      rounding_difference: Number(values.rounding_difference ?? 0),
      lines: validLines,
    };
  };

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      if (dupState === 'block') return;
      const payload = buildPayload(values);
      if (!payload.lines.length) {
        message.error(t('errAddExpenseLine'));
        return;
      }
      for (let i = 0; i < lines.length; i++) {
        const row = lines[i];
        if (lineNeedsInputVatGl(row) && !row.input_vat_account_id) {
          message.error(t('errLineInputVatGl', { line: i + 1 }));
          return;
        }
        const prepaidProblem = prepaidLineProblemKey(row);
        if (prepaidProblem) {
          message.error(t('errLinePrepaid', { line: i + 1, problem: t(prepaidProblem) }));
          return;
        }
      }
      saveMutation.mutate(payload);
    } catch {
      /* validation */
    }
  };

  const updateLine = (key: string, patch: Partial<LineRow>) => {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  };

  const onVatChange = (key: string, vatId?: string) => {
    const vc = vatOptions.find((v) => v.value === vatId);
    const patch: Partial<LineRow> = {
      vat_configuration_id: vatId,
      vat_rate: vc?.rate ?? 0,
      tax_amount: undefined,
    };
    if (!vatId || (vc?.rate ?? 0) <= 0) {
      patch.input_vat_account_id = undefined;
    } else if (vc?.suggestedGlAccountId && inputVatGlOptions.some((o) => o.value === vc.suggestedGlAccountId)) {
      patch.input_vat_account_id = vc.suggestedGlAccountId;
    }
    updateLine(key, patch);
  };

  const updatePrepaidConfig = (row: LineRow, patch: NonNullable<LineRow['prepaid_config']>) => {
    updateLine(row.key, { prepaid_config: { ...(row.prepaid_config ?? {}), ...patch } });
  };

  const onTreatmentChange = (row: LineRow, treatment: string) => {
    if (treatment !== PREPAID) {
      updateLine(row.key, { expense_treatment: 'NORMAL_EXPENSE', prepaid_asset_account_id: null, prepaid_config: null });
      return;
    }
    updateLine(row.key, {
      expense_treatment: PREPAID,
      prepaid_config: row.prepaid_config ?? { recognition_method: 'DAILY_PRO_RATA', day_count_method: 'ACTUAL_CALENDAR_DAYS', partial_month_policy: 'PRO_RATA' },
    });
  };

  const onPrepaidCategoryChange = (row: LineRow, categoryId?: string) => {
    const cat = prepaidCategories.find((c) => c.id === categoryId);
    updateLine(row.key, {
      prepaid_asset_account_id: row.prepaid_asset_account_id || cat?.default_prepaid_asset_account_id || null,
      expense_account_id: row.expense_account_id || cat?.default_expense_account_id || '',
      prepaid_config: {
        ...(row.prepaid_config ?? {}),
        category_id: categoryId ?? null,
        recognition_method:
          cat?.default_recognition_method && cat.default_recognition_method !== 'CUSTOM_SCHEDULE'
            ? cat.default_recognition_method
            : row.prepaid_config?.recognition_method,
        posting_mode: cat?.default_posting_mode ?? row.prepaid_config?.posting_mode,
      },
    });
  };

  const renderPrepaidSettings = (row: LineRow) => {
    const cfg = row.prepaid_config ?? {};
    const problem = prepaidLineProblemKey(row);
    return (
      <div style={{ padding: '4px 8px' }}>
        <Text strong>{t('prepaidRecognition')}</Text>
        <Text type="secondary" style={{ marginLeft: 8 }}>
          {t('prepaidRecognitionHint')}
        </Text>
        <Row gutter={12} style={{ marginTop: 8 }}>
          <Col span={6}>
            <Text type="secondary">{t('category')}</Text>
            <Select
              allowClear
              style={{ width: '100%' }}
              value={cfg.category_id || undefined}
              options={prepaidCategories.map((c) => ({ value: c.id, label: c.name }))}
              onChange={(v) => onPrepaidCategoryChange(row, v)}
            />
          </Col>
          <Col span={6}>
            <Text type="secondary">{t('prepaidAssetAccount')} *</Text>
            <Select
              showSearch
              optionFilterProp="label"
              style={{ width: '100%' }}
              status={!row.prepaid_asset_account_id ? 'error' : undefined}
              value={row.prepaid_asset_account_id || undefined}
              options={prepaidAssetOptions}
              onChange={(v) => updateLine(row.key, { prepaid_asset_account_id: v })}
            />
          </Col>
          <Col span={6}>
            <Text type="secondary">{t('servicePeriod')} *</Text>
            <DatePicker.RangePicker
              style={{ width: '100%' }}
              status={!cfg.service_start_date || !cfg.service_end_date ? 'error' : undefined}
              value={cfg.service_start_date && cfg.service_end_date ? [dayjs(cfg.service_start_date), dayjs(cfg.service_end_date)] : null}
              onChange={(r) =>
                updatePrepaidConfig(row, {
                  service_start_date: r?.[0] ? r[0].format('YYYY-MM-DD') : undefined,
                  service_end_date: r?.[1] ? r[1].format('YYYY-MM-DD') : undefined,
                })
              }
            />
          </Col>
          <Col span={6}>
            <Text type="secondary">{t('recognitionMethod')}</Text>
            <Select
              style={{ width: '100%' }}
              value={cfg.recognition_method || undefined}
              options={LINE_METHODS.map((m) => ({ value: m, label: t(METHOD_I18N[m] ?? m) }))}
              onChange={(v) => updatePrepaidConfig(row, { recognition_method: v })}
            />
          </Col>
          <Col span={6} style={{ marginTop: 8 }}>
            <Text type="secondary">{t('dayCount')}</Text>
            <Select
              style={{ width: '100%' }}
              value={cfg.day_count_method || undefined}
              options={DAY_COUNT_METHODS.map((m) => ({ value: m, label: t(DAY_COUNT_I18N[m] ?? m) }))}
              onChange={(v) => updatePrepaidConfig(row, { day_count_method: v })}
            />
          </Col>
          <Col span={6} style={{ marginTop: 8 }}>
            <Text type="secondary">{t('partialMonth')}</Text>
            <Select
              style={{ width: '100%' }}
              value={cfg.partial_month_policy || undefined}
              options={PARTIAL_MONTH_POLICIES.map((m) => ({ value: m, label: t(PARTIAL_MONTH_I18N[m] ?? m) }))}
              onChange={(v) => updatePrepaidConfig(row, { partial_month_policy: v })}
            />
          </Col>
          <Col span={6} style={{ marginTop: 8 }}>
            <Text type="secondary">{t('postingMode')}</Text>
            <Select
              allowClear
              placeholder={t('policyDefault')}
              style={{ width: '100%' }}
              value={cfg.posting_mode || undefined}
              options={POSTING_MODES.map((m) => ({ value: m, label: t(POSTING_MODE_I18N[m] ?? m) }))}
              onChange={(v) => updatePrepaidConfig(row, { posting_mode: v ?? null })}
            />
          </Col>
        </Row>
        {problem && (
          <Text type="danger" style={{ display: 'block', marginTop: 6 }}>
            {t('requiredField', { field: t(problem) })}
          </Text>
        )}
      </div>
    );
  };

  const lineNeedsInputVatGl = (row: LineRow) => {
    if (!row.vat_configuration_id) return false;
    const rate = row.vat_rate ?? vatOptions.find((v) => v.value === row.vat_configuration_id)?.rate ?? 0;
    return rate > 0;
  };

  if (isEdit && isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(isEdit ? `/fin/direct-purchase-invoices/${id}` : '/fin/direct-purchase-invoices')}>
          {tc('back')}
        </Button>
        <Title level={4} style={{ margin: 0 }}>
          {isEdit ? t('editDirectPurchaseInvoice') : t('newDirectPurchaseInvoice')}
        </Title>
      </Space>
      {dupState && (
        <Alert
          type={dupState === 'block' ? 'error' : 'warning'}
          message={dupState === 'block' ? t('duplicateSupplierInvoiceBlocked') : t('duplicateSupplierInvoiceWarning')}
          style={{ marginBottom: 12 }}
          showIcon
        />
      )}
      {vatConfigsError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message={t('couldNotLoadVatCodes')}
          description={(vatConfigsLoadError as any)?.response?.data?.message || (vatConfigsLoadError as Error)?.message}
        />
      )}
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          supplier_invoice_date: dayjs(),
          received_date: dayjs(),
          due_date: dayjs().add(30, 'day'),
          discount_amount: 0,
          freight_charges: 0,
          other_charges: 0,
          rounding_difference: 0,
        }}
      >
        <Card title={t('supplierInvoiceHeader')} style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="supplier_id" label={t('supplier')} rules={[{ required: true }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={suppliers.map((s: any) => ({ value: s.id, label: s.supplier_name }))}
                  onChange={(sid) => {
                    const num = form.getFieldValue('supplier_invoice_number');
                    checkDuplicate(sid, num);
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="supplier_invoice_number" label={t('supplierInvoiceNo')} rules={[{ required: true }]}>
                <Input
                  onBlur={(e) => checkDuplicate(form.getFieldValue('supplier_id'), e.target.value)}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="debit_account_id"
                label={t('apCreditAccount')}
                extra={t('apCreditAccountExtra')}
              >
                <Select allowClear showSearch optionFilterProp="label" options={apOptions} placeholder={t('default2000')} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="supplier_invoice_date" label={t('supplierInvoiceDate')} rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="received_date" label={t('receivedDate')} rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="due_date" label={t('dueDate')} rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="posting_date" label={t('postingDate')}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="department_id"
                label={t('department')}
                rules={[{ required: true, message: t('departmentRequired') }]}
              >
                <DepartmentSelect />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="notes" label={t('narrationRemarks')}>
                <TextArea rows={2} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card
          title={t('expenseLinesAllocation')}
          extra={
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => setLines((p) => [...p, { key: String(Date.now()), expense_account_id: '', amount: 0 }])}
            >
              {t('addLine')}
            </Button>
          }
          style={{ marginBottom: 16 }}
        >
          <Table
            size="small"
            pagination={false}
            rowKey="key"
            scroll={{ x: 1550 }}
            dataSource={lines}
            expandable={{
              expandedRowKeys: lines.filter((l) => l.expense_treatment === PREPAID).map((l) => l.key),
              rowExpandable: (row) => row.expense_treatment === PREPAID,
              expandedRowRender: renderPrepaidSettings,
              showExpandColumn: false,
            }}
            columns={[
              {
                title: t('treatment'),
                key: 'treatment',
                width: 150,
                render: (_, row) => (
                  <Select
                    style={{ width: '100%' }}
                    value={row.expense_treatment === PREPAID ? PREPAID : 'NORMAL_EXPENSE'}
                    options={[
                      { value: 'NORMAL_EXPENSE', label: t('expense') },
                      { value: PREPAID, label: t('prepaidExpense') },
                    ]}
                    onChange={(v) => onTreatmentChange(row, v)}
                  />
                ),
              },
              {
                title: t('expenseAccount'),
                key: 'exp',
                width: 220,
                render: (_, row) => (
                  <Select
                    style={{ width: '100%' }}
                    showSearch
                    optionFilterProp="label"
                    value={row.expense_account_id || undefined}
                    options={expenseOptions}
                    onChange={(v) => updateLine(row.key, { expense_account_id: v })}
                  />
                ),
              },
              {
                title: t('description'),
                key: 'desc',
                width: 280,
                render: (_, row) => (
                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <Input value={row.item_description} onChange={(e) => updateLine(row.key, { item_description: e.target.value })} />
                    <TextArea
                      value={row.specifications ?? ''}
                      onChange={(e) => updateLine(row.key, { specifications: e.target.value })}
                      autoSize={{ minRows: 1, maxRows: 6 }}
                      maxLength={LINE_SPECIFICATIONS_MAX_LENGTH}
                      placeholder={t('technicalSpecOptional')}
                    />
                  </Space>
                ),
              },
              {
                title: t('costCenterProject'),
                key: 'project',
                width: 280,
                render: (_, row) => (
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    style={{ width: '100%' }}
                    popupMatchSelectWidth={false}
                    dropdownStyle={{ minWidth: 420 }}
                    placeholder={t('selectProject')}
                    value={row.project_id || undefined}
                    options={projectOptions}
                    optionRender={(option) => (
                      <div style={{ whiteSpace: 'normal', lineHeight: 1.35 }}>
                        {String(option.label ?? '')}
                      </div>
                    )}
                    onChange={(v) => updateLine(row.key, { project_id: v })}
                  />
                ),
              },
              {
                title: t('amount'),
                key: 'amt',
                width: 110,
                render: (_, row) => (
                  <InputNumber
                    min={0}
                    style={{ width: '100%' }}
                    value={row.amount}
                    onChange={(v) => updateLine(row.key, { amount: Number(v) || 0 })}
                  />
                ),
              },
              {
                title: t('vatCode'),
                key: 'vat',
                width: 220,
                render: (_, row) => (
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    style={{ width: '100%' }}
                    placeholder={t('selectVat')}
                    value={row.vat_configuration_id}
                    options={vatOptions}
                    onChange={(v) => onVatChange(row.key, v)}
                  />
                ),
              },
              {
                title: t('inputVatGl'),
                key: 'vat_gl',
                width: 220,
                render: (_, row) => {
                  const required = lineNeedsInputVatGl(row);
                  return (
                    <Select
                      allowClear={!required}
                      showSearch
                      optionFilterProp="label"
                      style={{ width: '100%' }}
                      placeholder={required ? t('requiredWhenVat') : '—'}
                      disabled={!required}
                      value={required ? row.input_vat_account_id || undefined : undefined}
                      options={inputVatGlOptions}
                      status={required && !row.input_vat_account_id ? 'error' : undefined}
                      onChange={(v) => updateLine(row.key, { input_vat_account_id: v })}
                    />
                  );
                },
              },
              {
                title: t('inclShort'),
                key: 'inc',
                width: 56,
                render: (_, row) => (
                  <Checkbox checked={!!row.tax_inclusive} onChange={(e) => updateLine(row.key, { tax_inclusive: e.target.checked })} />
                ),
              },
              {
                title: '',
                key: 'del',
                width: 48,
                render: (_, row) =>
                  lines.length > 1 ? (
                    <Button type="link" danger icon={<DeleteOutlined />} onClick={() => setLines((p) => p.filter((l) => l.key !== row.key))} />
                  ) : null,
              },
            ]}
          />
          <Text type="secondary" style={{ display: 'block' }}>
            {t('lineTotalsBeforeHeader', {
              sub: lineTotals.sub.toFixed(2),
              tax: lineTotals.tax.toFixed(2),
              gross: lineTotals.gross.toFixed(2),
            })}
          </Text>
          <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
            {t('vatGlHint')}
          </Text>
        </Card>

        <Card title={t('totals')} style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="discount_amount" label={t('discount')}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="freight_charges" label={t('freight')}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="other_charges" label={t('other')}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="rounding_difference" label={t('rounding')}>
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Space>
          <Button type="primary" icon={<SaveOutlined />} loading={saveMutation.isPending} onClick={onSave}>
            {tc('save')}
          </Button>
        </Space>
      </Form>
    </div>
  );
};

export default DirectPurchaseInvoiceFormPage;
